import { queryClient } from '../../queryClient';
import Task, { TaskStage } from '../../types/Task';
import { TaskType } from '../../types/TaskType';
import { TaskTableDataType } from '../../components/TaskTable/types/TaskTableTypes';
import { queryKeys } from '../queryKeys';

type CachedTask = Task | TaskTableDataType;

const defaultKey = (spaceId: number) => ['tasks', spaceId] as const;
const archiveKey = (spaceId: number) => ['tasks', spaceId, 'archive'] as const;

/**
 * The list cache this task currently lives in. Archived tasks are held in a cache the
 * default list never sees, so patching `tasks` for an archived task is a silent no-op
 * and the Archive view keeps showing the pre-edit row.
 */
export function taskListCacheKey(task: Pick<Task, 'type' | 'stage' | 'workspaceId'>) {
  if (task.type === TaskType.epic) return queryKeys.epics(task.workspaceId);
  return task.stage === 'archive' ? queryKeys.archivedTasks(task.workspaceId) : queryKeys.tasks(task.workspaceId);
}

// The archive cache is a separate query because the BE excludes archived tasks
// from the default list. Boundary crossings (into/out of archive) require moving
// the row between caches; other stage changes patch the one cache the task lives in.
export function reconcileStageChange(
  spaceId: number,
  taskId: number,
  prevStage: TaskStage | undefined,
  next: Partial<Task> & { stage: TaskStage }
) {
  if (prevStage === next.stage) return;

  const enteringArchive = next.stage === 'archive' && prevStage !== 'archive';
  const leavingArchive = prevStage === 'archive' && next.stage !== 'archive';

  if (enteringArchive) {
    queryClient.setQueryData<CachedTask[]>(defaultKey(spaceId), (old) =>
      old ? old.filter((t) => t.id !== taskId) : old
    );
    void queryClient.invalidateQueries({ queryKey: archiveKey(spaceId) });
    return;
  }

  if (leavingArchive) {
    queryClient.setQueryData<CachedTask[]>(archiveKey(spaceId), (old) =>
      old ? old.filter((t) => t.id !== taskId) : old
    );
    void queryClient.invalidateQueries({ queryKey: defaultKey(spaceId) });
    return;
  }

  queryClient.setQueryData<CachedTask[]>(defaultKey(spaceId), (old) =>
    old ? old.map((t) => (t.id === taskId ? { ...t, ...next } : t)) : old
  );
}
